/** Where the presentation video lives — the thumbnail and the link open it. */
export const PROSPECTING_IN_CHAT_VIDEO_URL =
  "https://www.youtube.com/watch?v=hQftCpnhFcE";

/**
 * Last instant the announcement is shown: 30 October 2026, 23:59:59 in Paris
 * (CET, UTC+1 — summer time ends on the 25th). Past it, neither the banner nor
 * the modal render, whatever the user has seen.
 */
const ANNOUNCEMENT_ENDS_AT = new Date("2026-10-30T23:59:59+01:00");

/** Whether the announcement is still running at `now`. */
export function isProspectingAnnouncementActive(
  now: Date = new Date(),
): boolean {
  return now.getTime() <= ANNOUNCEMENT_ENDS_AT.getTime();
}

const KEY_PREFIX = "shaarp:announcement:prospecting-in-chat";

/**
 * Keyed by user, so a second account signed in on the same browser keeps its
 * own state. Browser storage is enough: seeing the announcement again on a new
 * device is harmless.
 */
function storageKey(
  kind: "seen" | "banner-dismissed",
  userId: string | undefined,
): string {
  const key = `${KEY_PREFIX}:${kind}`;
  return userId ? `${key}:${userId}` : key;
}

/** Storage can throw (private mode, blocked site data): treat it as unset. */
function hasFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  try {
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Nothing to do: the flag is simply forgotten on the next visit.
  }
}

/** Whether the user has already been shown the modal once. */
export function hasSeenProspectingAnnouncement(
  userId: string | undefined,
): boolean {
  return hasFlag(storageKey("seen", userId));
}

/** Records that the user has been shown the modal once. */
export function markProspectingAnnouncementSeen(
  userId: string | undefined,
): void {
  setFlag(storageKey("seen", userId));
}

/** Whether the user has closed the banner for good. */
export function hasDismissedProspectingBanner(
  userId: string | undefined,
): boolean {
  return hasFlag(storageKey("banner-dismissed", userId));
}

/** Records that the user closed the banner, so it stays closed. */
export function dismissProspectingBanner(userId: string | undefined): void {
  setFlag(storageKey("banner-dismissed", userId));
}
